import React, { useState, useEffect, useRef } from 'react';
import { z } from 'zod';
import { Pencil } from 'lucide-react';
import { PathInputWithAutocomplete } from './PathInputWithAutocomplete';
import { SchemaField } from '../../types/schemaMapperTypes';

interface FieldNameEditorProps {
  index: number;
  field: SchemaField;
  isEditing: boolean;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveEdit: (newName: string) => void;
  description?: string | undefined;
  destinationZod: z.ZodTypeAny;
}

export const FieldNameEditor: React.FC<FieldNameEditorProps> = React.memo(({ 
  index, 
  field, 
  isEditing, 
  onStartEditing, 
  onCancelEditing,
  onSaveEdit, 
  description,
  destinationZod
}) => {
  const [editValue, setEditValue] = useState(field.name);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Reset edit value when field name changes externally
  useEffect(() => {
    setEditValue(field.name);
  }, [field.name]);
  
  // Auto-focus the input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);
  
  const handleSave = () => {
    // Prevent empty field names
    if (editValue.trim() === '') {
      setEditValue(field.name); // Reset to original
      onCancelEditing();
      return;
    }
    
    onSaveEdit(editValue);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(field.name); // Reset to original
      onCancelEditing();
    }
  };
  
  return (
    <div className={isEditing ? "field-name-container-wrapper" : ""}>
      <div className={isEditing ? "field-name-input-row input-with-button-group" : "field-name-input-row"}>
        {isEditing ? (
          <>
            <div className="field-name-edit-mode" style={{ flexGrow: 1 }}>
              <div className="field-name-autocomplete-wrapper" onKeyDown={handleKeyDown}>
                <PathInputWithAutocomplete
                  value={editValue}
                  onChange={setEditValue}
                  sourceZod={destinationZod}
                />
              </div>
            </div>
            <div className="field-edit-buttons button-group">
              <button 
                type="button" 
                className="button-success subtle button-icon" 
                onClick={handleSave} 
                title="Save">
                <span>✓</span>
              </button>
              <button 
                type="button" 
                className="button-danger subtle button-icon" 
                onClick={() => {
                  setEditValue(field.name);
                  onCancelEditing();
                }} 
                title="Cancel">
                <span>✕</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="field-name-text">{field.name || <em>Unnamed Field</em>}</span>
            <button 
              type="button" 
              className="button-subtle" 
              onClick={onStartEditing} 
              title="Edit field name">
              <Pencil size={16} />
            </button>
          </>
        )}
      </div>
      {field.name && description && !isEditing && (
        <div className="field-description">
          {description}
        </div>
      )}
    </div>
  );
}); 